import React, { useState, useEffect } from 'react';
import { useLogo } from '../../contexts/LogoContext';
import { buildApiUrl } from '../../config/api';

/**
 * AppSplashScreen
 * 
 * Inspired by the dynamic ride-in delivery scooter splash screen:
 * - Rides in from left with dynamic air/wind trails and ground speed shadows.
 * - Gentle floating & vibration pulse to feel like a moving vehicle/dhol beat.
 * - In the background, sends a lightweight ping to the backend Lambda so it wakes up.
 * - Smoothly fades out with scale expansion when backend responds or after max timeout.
 */
const AppSplashScreen = () => {
  const { logoUrl } = useLogo();
  // Guaranteed actual BOW logo (uses dynamic S3 logoUrl or instant local high-res /logo512.png)
  const actualLogo = logoUrl || '/logo512.png';

  // Only display on initial session load so internal route transitions remain instant
  const alreadyShown = typeof window !== 'undefined' && sessionStorage.getItem('bow_splash_shown') === 'true';
  const [visible, setVisible] = useState(!alreadyShown);
  const [fading, setFading] = useState(false);
  const [statusText, setStatusText] = useState('Tuning the beats...');

  useEffect(() => {
    if (alreadyShown) return;
    sessionStorage.setItem('bow_splash_shown', 'true');

    let isMounted = true;
    const startTime = Date.now();
    const MIN_DISPLAY_TIME = 1600; // Let user enjoy the smooth animation (at least 1.6s)
    const MAX_TIMEOUT = 5000;      // Don't keep user waiting more than 5s under any circumstance

    // Dynamic status text updates
    const textTimer1 = setTimeout(() => {
      if (isMounted) setStatusText('Waking up Beats of Washington...');
    }, 1200);

    const textTimer2 = setTimeout(() => {
      if (isMounted) setStatusText('Connecting to cloud...');
    }, 2800);

    // Warm up the AWS Lambda backend
    const warmUpBackend = async () => {
      try {
        const pingUrl = buildApiUrl('/events'); // lightweight endpoint that warms up Express + Lambda
        await fetch(pingUrl, { method: 'GET', mode: 'cors' });
      } catch (err) {
        // Even if network blips, the app will continue seamlessly
        console.log('Background lambda warmup ping initiated');
      } finally {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, MIN_DISPLAY_TIME - elapsed);

        setTimeout(() => {
          if (isMounted) {
            setFading(true);
            setTimeout(() => {
              if (isMounted) setVisible(false);
            }, 600); // Wait for fade-out CSS transition
          }
        }, remaining);
      }
    };

    warmUpBackend();

    // Absolute fallback timer
    const fallbackTimer = setTimeout(() => {
      if (isMounted) {
        setFading(true);
        setTimeout(() => {
          if (isMounted) setVisible(false);
        }, 600);
      }
    }, MAX_TIMEOUT);

    return () => {
      isMounted = false;
      clearTimeout(textTimer1);
      clearTimeout(textTimer2);
      clearTimeout(fallbackTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gradient-to-b from-gray-950 via-[#0d0d12] to-gray-950 text-white transition-all duration-700 pointer-events-none select-none overflow-hidden ${
        fading ? 'opacity-0 scale-105 backdrop-blur-0' : 'opacity-100 scale-100 backdrop-blur-md'
      }`}
      style={{ willChange: 'opacity, transform' }}
    >
      {/* Background ambient lighting */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-orange-600/20 rounded-full blur-[100px] animate-pulse pointer-events-none" />
        <div className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-amber-500/15 rounded-full blur-[80px] pointer-events-none" />
      </div>

      {/* Main Riding Stage */}
      <div className="relative flex flex-col items-center justify-center w-full max-w-sm px-6">
        
        {/* The Animated Rider Wrapper */}
        <div className="relative flex flex-col items-center bow-rider-container">
          
          {/* Air Flow / Speed Lines Behind Logo */}
          <div className="absolute -left-16 sm:-left-20 top-1/2 -translate-y-1/2 flex flex-col gap-2 pointer-events-none z-0">
            <span className="bow-airflow-line bow-airflow-1" />
            <span className="bow-airflow-line bow-airflow-2" />
            <span className="bow-airflow-line bow-airflow-3" />
            <span className="bow-airflow-line bow-airflow-4" />
          </div>

          {/* Actual BOW Logo with 3D Float, Bobbing, and Glow */}
          <div className="relative z-10 bow-rider-logo">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full p-1 bg-gradient-to-tr from-amber-500 via-orange-500 to-yellow-400 shadow-[0_10px_35px_rgba(249,115,22,0.45)] ring-4 ring-orange-500/30 flex items-center justify-center overflow-hidden bg-white">
              <img
                src={actualLogo}
                alt="Beats of Washington"
                className="w-full h-full object-cover rounded-full"
                onError={(e) => {
                  e.target.src = '/logo512.png';
                }}
              />
            </div>

            {/* Sparkle badge */}
            <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-amber-400 text-gray-950 flex items-center justify-center text-xs font-bold shadow-lg animate-bounce">
              ⚡
            </div>
          </div>

          {/* Ground Speed Shadow (pulsating & squashing as logo bobs) */}
          <div className="mt-4 w-20 sm:w-24 h-3 bg-black/60 rounded-full blur-[3px] bow-rider-shadow" />
        </div>

        {/* Dynamic Road / Speed Track line underneath */}
        <div className="relative w-48 sm:w-56 h-1 mt-3 bg-gray-800/80 rounded-full overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-orange-500 to-transparent bow-road-runner" />
        </div>

        {/* Brand Text and Status */}
        <div className="mt-8 text-center flex flex-col items-center">
          <h2 className="text-xl sm:text-2xl font-extrabold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-300 to-yellow-400 drop-shadow-sm">
            BEATS OF WASHINGTON
          </h2>
          
          <div className="mt-2.5 flex items-center gap-2 text-xs sm:text-sm text-gray-400 font-medium">
            <span className="w-2 h-2 rounded-full bg-orange-500 animate-ping" />
            <span>{statusText}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppSplashScreen;
